import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { motion } from 'motion/react';
import { spring } from '../motion/presets';
import { useReducedMotion } from '../motion/useReducedMotion';
import { cx } from '../internal/cx';

export type TabItem = { id: string; label: string; disabled?: boolean };

export type TabsProps = {
  tabs: TabItem[];
  /** id de l'onglet actif (contrôlé). */
  value: string;
  onChange: (id: string) => void;
  /** aria-label de la tablist. */
  label?: string;
  className?: string;
};

/** Petit chevron directionnel des indicateurs de débordement. */
function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d={dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
    </svg>
  );
}

/**
 * Onglets contrôlés : `role="tablist"`, roving tabindex (flèches ←/→, Home/End),
 * soulignement glissant. L'indicateur est un élément unique MESURÉ (left/width
 * animés) — PAS un `layoutId` : une animation de layout partagée dans un
 * sous-arbre en cours d'exit bloque l'`AnimatePresence` des pages (la page
 * suivante restait à opacity 0).
 *
 * Débordement (mobile étroit — spec 08) : la tablist défile horizontalement
 * (scrollbar masquée) ; un dégradé + chevron apparaît à gauche/droite tant qu'il
 * reste du contenu masqué de ce côté. Le dégradé laisse passer le swipe
 * (`pointer-events-none`) ; seul le chevron est cliquable pour défiler d'un cran.
 * L'onglet actif est toujours ramené dans la zone visible.
 */
export function Tabs({ tabs, value, onChange, label, className }: TabsProps) {
  const uid = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);
  const [overflow, setOverflow] = useState<{ left: boolean; right: boolean }>({
    left: false,
    right: false,
  });

  const enabled = tabs.filter((t) => !t.disabled);

  const updateOverflow = (list: HTMLDivElement) => {
    const max = list.scrollWidth - list.clientWidth;
    const left = list.scrollLeft > 1;
    const right = list.scrollLeft < max - 1;
    setOverflow((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  };

  const scrollBySide = (dir: -1 | 1) => {
    const list = listRef.current;
    if (!list || typeof list.scrollBy !== 'function') return;
    list.scrollBy({ left: dir * list.clientWidth * 0.75, behavior: reduced ? 'auto' : 'smooth' });
  };

  // Position de l'indicateur sous l'onglet actif + état de débordement, re-mesurés
  // au resize (ResizeObserver) — voir aussi le listener de scroll ci-dessous.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const btn = list.querySelector<HTMLButtonElement>(`[data-tab-id="${CSS.escape(value)}"]`);
      if (!btn) {
        setIndicator(null);
      } else {
        // inset-x-2 d'origine : 8px de retrait de chaque côté.
        setIndicator({ left: btn.offsetLeft + 8, width: Math.max(0, btn.offsetWidth - 16) });
      }
      updateOverflow(list);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return; // jsdom
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [value, tabs]);

  // Débordement recalculé à chaque défilement (affordances gauche/droite).
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const onScroll = () => updateOverflow(list);
    list.addEventListener('scroll', onScroll, { passive: true });
    return () => list.removeEventListener('scroll', onScroll);
  }, []);

  // Ramène l'onglet actif dans la zone visible (clic sur un onglet à moitié
  // caché, ou deep-link vers la dernière section) sans faire défiler la page.
  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof list.scrollTo !== 'function') return;
    const btn = list.querySelector<HTMLButtonElement>(`[data-tab-id="${CSS.escape(value)}"]`);
    if (!btn) return;
    const behavior: ScrollBehavior = reduced ? 'auto' : 'smooth';
    const pad = 8;
    const left = btn.offsetLeft;
    const right = left + btn.offsetWidth;
    if (left - pad < list.scrollLeft) {
      list.scrollTo({ left: left - pad, behavior });
    } else if (right + pad > list.scrollLeft + list.clientWidth) {
      list.scrollTo({ left: right + pad - list.clientWidth, behavior });
    }
  }, [value, reduced]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = enabled.findIndex((t) => t.id === value);
    if (currentIndex === -1 || enabled.length === 0) return;
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % enabled.length;
    else if (event.key === 'ArrowLeft')
      nextIndex = (currentIndex - 1 + enabled.length) % enabled.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = enabled.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const next = enabled[nextIndex] as TabItem;
    onChange(next.id);
    listRef.current?.querySelector<HTMLButtonElement>(`[data-tab-id="${next.id}"]`)?.focus();
  };

  return (
    <div className={cx('relative border-b border-ink-600/60', className)}>
      <div
        ref={listRef}
        role="tablist"
        aria-label={label}
        onKeyDown={handleKeyDown}
        className="relative flex items-center gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((tab) => {
          const selected = tab.id === value;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              data-tab-id={tab.id}
              aria-selected={selected}
              aria-controls={`${uid}-panel-${tab.id}`}
              id={`${uid}-tab-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              disabled={tab.disabled}
              onClick={() => onChange(tab.id)}
              className={cx(
                'relative min-h-[2.75rem] shrink-0 whitespace-nowrap px-4 py-2 font-display text-sm font-semibold transition-colors duration-fast ease-fun',
                selected ? 'text-panda-500' : 'text-ink-400 hover:text-ink-100',
                'disabled:cursor-not-allowed disabled:opacity-40',
              )}
            >
              {tab.label}
            </button>
          );
        })}
        {indicator && (
          <motion.span
            aria-hidden="true"
            initial={false}
            animate={{ left: indicator.left, width: indicator.width }}
            transition={reduced ? { duration: 0 } : spring.soft}
            className="absolute bottom-0 h-0.5 rounded-full bg-panda-500"
          />
        )}
      </div>

      {/* Affordance gauche : du contenu est masqué à gauche. Le dégradé laisse
          passer le swipe ; le chevron défile d'un cran au clic (souris/clavier
          non concernés : navigation par flèches sur les onglets). */}
      <div
        aria-hidden="true"
        className={cx(
          'pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-start bg-gradient-to-r from-app to-transparent transition-opacity duration-fast ease-fun',
          overflow.left ? 'opacity-100' : 'opacity-0',
        )}
      >
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => scrollBySide(-1)}
          className={cx(
            'grid h-7 w-7 place-items-center rounded-full text-ink-400 transition-colors duration-fast ease-fun hover:text-ink-100',
            overflow.left ? 'pointer-events-auto' : 'pointer-events-none',
          )}
        >
          <Chevron dir="left" />
        </button>
      </div>

      {/* Affordance droite : du contenu est masqué à droite. */}
      <div
        aria-hidden="true"
        className={cx(
          'pointer-events-none absolute inset-y-0 right-0 flex w-10 items-center justify-end bg-gradient-to-l from-app to-transparent transition-opacity duration-fast ease-fun',
          overflow.right ? 'opacity-100' : 'opacity-0',
        )}
      >
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => scrollBySide(1)}
          className={cx(
            'grid h-7 w-7 place-items-center rounded-full text-ink-400 transition-colors duration-fast ease-fun hover:text-ink-100',
            overflow.right ? 'pointer-events-auto' : 'pointer-events-none',
          )}
        >
          <Chevron dir="right" />
        </button>
      </div>
    </div>
  );
}
